import {
  ACESFilmicToneMapping,
  BoxGeometry,
  Color,
  DirectionalLight,
  DoubleSide,
  Group,
  Mesh,
  PerspectiveCamera,
  PlaneGeometry,
  PCFShadowMap,
  Scene,
  ShaderMaterial,
  ShadowMaterial,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three'

import { ChainSimulator } from './ChainSimulator'
import {
  createRibbonTuning,
  RIBBON_CONFIG,
  type RibbonTuning,
} from './config'
import { dampProgress, clampProgress } from './scrollProgress'
import {
  ribbonDepthFragmentShader,
  ribbonDepthVertexShader,
  ribbonFragmentShader,
  ribbonVertexShader,
} from './shaders'
import { createChainTexture, createFabricNormalTexture } from './textures'

export type { RibbonTuning } from './config'

export interface RibbonDiagnostics {
  clipped: boolean
  currentProgress: number
  fps: number
  frameCorrections: number
  frameMedianMs: number
  frameP95Ms: number
  maxConstraintError: number
  minAdjacentFrameDot: number
  ndcExtentX: number
  ndcExtentY: number
  reducedMotion: boolean
  renderCalls: number
  slowFrameRatio: number
  triangles: number
  targetProgress: number
}

type RibbonUniforms = Record<
  | 'uAmbient'
  | 'uBaseColor'
  | 'uChainTexture'
  | 'uFabricNormalMap'
  | 'uFabricRepeat'
  | 'uFabricStrength'
  | 'uFrameTexture'
  | 'uJointCount'
  | 'uLightDirection'
  | 'uRibbonLength'
  | 'uRibbonWidth'
  | 'uSpecularPower'
  | 'uTime'
  | 'uTurbulenceAmplitude'
  | 'uTurbulenceScale'
  | 'uTurbulenceSpeed',
  { value: unknown }
>

const median = (values: readonly number[]): number => {
  if (values.length === 0) {
    return 0
  }

  const sorted = [...values].sort((first, second) => first - second)
  const midpoint = Math.floor(sorted.length / 2)

  return sorted.length % 2 === 0
    ? (sorted[midpoint - 1] + sorted[midpoint]) * 0.5
    : sorted[midpoint]
}

const percentile = (
  values: readonly number[],
  requestedPercentile: number,
): number => {
  if (values.length === 0) {
    return 0
  }

  const sorted = [...values].sort((first, second) => first - second)
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil(sorted.length * requestedPercentile) - 1),
  )
  return sorted[index]
}

export class RibbonScene {
  readonly tuning: RibbonTuning

  private readonly camera: PerspectiveCamera
  private readonly chain: ChainSimulator
  private readonly chainTexture
  private readonly fabricNormalTexture
  private readonly frameTexture
  private readonly frameTimes: number[] = []
  private readonly group = new Group()
  private readonly light: DirectionalLight
  private readonly mesh: Mesh<BoxGeometry, ShaderMaterial>
  private readonly ndcScratch = new Vector3()
  private readonly receiver: Mesh<PlaneGeometry, ShadowMaterial>
  private readonly renderer: WebGLRenderer
  private readonly scene = new Scene()
  private readonly uniforms: RibbonUniforms

  private animationFrame: number | null = null
  private currentProgress = 0
  private height = 1
  private lastFrameTime = 0
  private reducedMotion = false
  private targetProgress = 0
  private time = 0
  private width = 1

  constructor(canvas: HTMLCanvasElement) {
    this.tuning = createRibbonTuning()
    this.renderer = new WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas,
      powerPreference: 'high-performance',
      premultipliedAlpha: true,
    })
    this.renderer.outputColorSpace = SRGBColorSpace
    this.renderer.toneMapping = ACESFilmicToneMapping
    this.renderer.toneMappingExposure = RIBBON_CONFIG.renderer.exposure
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = PCFShadowMap
    this.renderer.setClearColor(0x000000, 0)

    this.camera = new PerspectiveCamera(
      RIBBON_CONFIG.camera.fov,
      1,
      RIBBON_CONFIG.camera.near,
      RIBBON_CONFIG.camera.far,
    )
    this.camera.position.set(0, 0, this.tuning.cameraDistanceDesktop)
    this.camera.lookAt(0, RIBBON_CONFIG.camera.focusY, 0)

    this.chain = new ChainSimulator(
      RIBBON_CONFIG.geometry.joints,
      RIBBON_CONFIG.geometry.length,
      this.tuning,
    )
    this.chainTexture = createChainTexture(
      this.chain.positionTextureData,
      RIBBON_CONFIG.geometry.joints,
    )
    this.frameTexture = createChainTexture(
      this.chain.frameTextureData,
      RIBBON_CONFIG.geometry.joints,
    )
    this.fabricNormalTexture = createFabricNormalTexture(
      RIBBON_CONFIG.material.normalMapSize,
    )
    this.fabricNormalTexture.anisotropy = Math.min(
      8,
      this.renderer.capabilities.getMaxAnisotropy(),
    )

    this.uniforms = {
      uAmbient: { value: RIBBON_CONFIG.material.ambient },
      uBaseColor: { value: new Color(RIBBON_CONFIG.colors.ribbon) },
      uChainTexture: { value: this.chainTexture },
      uFabricNormalMap: { value: this.fabricNormalTexture },
      uFabricRepeat: {
        value: new Vector2(...RIBBON_CONFIG.material.fabricRepeat),
      },
      uFabricStrength: { value: this.tuning.fabricStrength },
      uFrameTexture: { value: this.frameTexture },
      uJointCount: { value: RIBBON_CONFIG.geometry.joints },
      uLightDirection: {
        value: new Vector3(...RIBBON_CONFIG.material.lightDirection).normalize(),
      },
      uRibbonLength: { value: RIBBON_CONFIG.geometry.length },
      uRibbonWidth: { value: RIBBON_CONFIG.geometry.width },
      uSpecularPower: { value: RIBBON_CONFIG.material.specularPower },
      uTime: { value: 0 },
      uTurbulenceAmplitude: { value: this.tuning.turbulenceAmplitude },
      uTurbulenceScale: { value: this.tuning.turbulenceScale },
      uTurbulenceSpeed: { value: this.tuning.turbulenceSpeed },
    }

    const geometry = new BoxGeometry(
      RIBBON_CONFIG.geometry.length,
      RIBBON_CONFIG.geometry.width,
      RIBBON_CONFIG.geometry.thickness,
      RIBBON_CONFIG.geometry.lengthSegments,
      RIBBON_CONFIG.geometry.widthSegments,
      RIBBON_CONFIG.geometry.thicknessSegments,
    )
    const material = new ShaderMaterial({
      fragmentShader: ribbonFragmentShader,
      uniforms: this.uniforms,
      vertexShader: ribbonVertexShader,
    })
    const depthMaterial = new ShaderMaterial({
      fragmentShader: ribbonDepthFragmentShader,
      uniforms: this.createDepthUniforms(),
      vertexShader: ribbonDepthVertexShader,
    })

    this.mesh = new Mesh(geometry, material)
    this.mesh.castShadow = true
    this.mesh.customDepthMaterial = depthMaterial
    this.mesh.frustumCulled = false
    this.group.add(this.mesh)
    this.scene.add(this.group)

    this.light = new DirectionalLight(0xffffff, 2.4)
    this.light.position.set(...RIBBON_CONFIG.light.position)
    this.light.castShadow = true
    this.light.shadow.bias = RIBBON_CONFIG.light.shadowBias
    this.light.shadow.normalBias = RIBBON_CONFIG.light.shadowNormalBias
    this.light.shadow.mapSize.setScalar(RIBBON_CONFIG.light.shadowMapSize)
    this.light.shadow.radius = 5
    this.light.shadow.camera.left = -RIBBON_CONFIG.light.shadowBounds
    this.light.shadow.camera.right = RIBBON_CONFIG.light.shadowBounds
    this.light.shadow.camera.top = RIBBON_CONFIG.light.shadowBounds
    this.light.shadow.camera.bottom = -RIBBON_CONFIG.light.shadowBounds
    this.light.shadow.camera.near = 0.1
    this.light.shadow.camera.far = 40
    this.light.target.position.set(0, 0, RIBBON_CONFIG.light.shadowPlaneDepth)
    this.scene.add(this.light, this.light.target)

    this.receiver = new Mesh(
      new PlaneGeometry(36, 30),
      new ShadowMaterial({
        color: new Color(RIBBON_CONFIG.colors.shadow),
        opacity: this.tuning.shadowOpacity,
        side: DoubleSide,
        transparent: true,
      }),
    )
    this.receiver.position.z = RIBBON_CONFIG.light.shadowPlaneDepth
    this.receiver.receiveShadow = true
    this.scene.add(this.receiver)
  }

  dispose(): void {
    this.stop()
    this.mesh.geometry.dispose()
    this.mesh.material.dispose()
    this.mesh.customDepthMaterial?.dispose()
    this.receiver.geometry.dispose()
    this.receiver.material.dispose()
    this.chainTexture.dispose()
    this.frameTexture.dispose()
    this.fabricNormalTexture.dispose()
    this.light.shadow.map?.dispose()
    this.renderer.dispose()
  }

  getDiagnostics(): RibbonDiagnostics {
    const chainDiagnostics = this.chain.getDiagnostics()
    const frameMedianMs = median(this.frameTimes)
    const frameP95Ms = percentile(this.frameTimes, 0.95)
    const [ndcExtentX, ndcExtentY] = this.measureNdcExtents()
    const slowFrameCount = this.frameTimes.filter(
      (frameTime) => frameTime > 25,
    ).length

    return {
      clipped: ndcExtentX > 0.98 || ndcExtentY > 0.98,
      currentProgress: this.currentProgress,
      fps: frameMedianMs > 0 ? 1000 / frameMedianMs : 0,
      frameCorrections: chainDiagnostics.frameCorrections,
      frameMedianMs,
      frameP95Ms,
      maxConstraintError: chainDiagnostics.maxConstraintError,
      minAdjacentFrameDot: chainDiagnostics.minAdjacentFrameDot,
      ndcExtentX,
      ndcExtentY,
      reducedMotion: this.reducedMotion,
      renderCalls: this.renderer.info.render.calls,
      slowFrameRatio:
        this.frameTimes.length > 0
          ? slowFrameCount / this.frameTimes.length
          : 0,
      targetProgress: this.targetProgress,
      triangles: this.renderer.info.render.triangles,
    }
  }

  resize(width: number, height: number): void {
    this.width = Math.max(1, width)
    this.height = Math.max(1, height)
    const aspect = this.width / this.height
    const isMobileAspect =
      aspect < RIBBON_CONFIG.camera.mobileAspectBreakpoint
    const isMobileDevice =
      this.width <= RIBBON_CONFIG.renderer.mobileWidthBreakpoint
    const pixelRatioCap = isMobileDevice
      ? RIBBON_CONFIG.renderer.mobileDprCap
      : RIBBON_CONFIG.renderer.desktopDprCap

    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio || 1, pixelRatioCap),
    )
    this.renderer.setSize(this.width, this.height, false)
    this.camera.aspect = aspect
    this.camera.position.z = isMobileAspect
      ? this.tuning.cameraDistanceMobile
      : this.tuning.cameraDistanceDesktop
    this.camera.lookAt(0, RIBBON_CONFIG.camera.focusY, 0)
    this.camera.updateProjectionMatrix()
    this.group.rotation.z = isMobileAspect
      ? RIBBON_CONFIG.camera.mobileRotation
      : 0
    this.group.updateMatrixWorld(true)
    this.render()
  }

  setProgress(progress: number, immediate = false): void {
    this.targetProgress = clampProgress(progress)

    if (immediate || this.reducedMotion) {
      this.currentProgress = this.targetProgress
      this.chain.reset(this.currentProgress)
      this.uploadChainTextures()
      this.render()
    }
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced

    if (reduced) {
      this.time = 0
      this.uniforms.uTime.value = 0
      this.setProgress(this.targetProgress, true)
    }
  }

  settle(frames = 120): void {
    const safeFrameCount = Math.max(1, Math.min(600, Math.round(frames)))

    for (let frame = 0; frame < safeFrameCount; frame += 1) {
      this.currentProgress = dampProgress(
        this.currentProgress,
        this.targetProgress,
        this.tuning.progressDamping,
        1 / 60,
      )
      this.chain.step(1 / 60, this.currentProgress, this.reducedMotion)
    }

    this.uploadChainTextures()
    this.render()
  }

  start(): void {
    if (this.animationFrame !== null) {
      return
    }

    this.lastFrameTime = performance.now()
    this.animationFrame = requestAnimationFrame(this.animate)
  }

  stop(): void {
    if (this.animationFrame === null) {
      return
    }

    cancelAnimationFrame(this.animationFrame)
    this.animationFrame = null
  }

  updateTuning<Key extends keyof RibbonTuning>(
    key: Key,
    value: RibbonTuning[Key],
  ): void {
    this.tuning[key] = value

    switch (key) {
      case 'fabricStrength':
        this.uniforms.uFabricStrength.value = value
        break
      case 'shadowOpacity':
        this.receiver.material.opacity = value
        break
      case 'turbulenceAmplitude':
        this.uniforms.uTurbulenceAmplitude.value = value
        break
      case 'turbulenceScale':
        this.uniforms.uTurbulenceScale.value = value
        break
      case 'turbulenceSpeed':
        this.uniforms.uTurbulenceSpeed.value = value
        break
      case 'cameraDistanceDesktop':
      case 'cameraDistanceMobile':
        this.resize(this.width, this.height)
        break
      default:
        break
    }
  }

  private readonly animate = (timestamp: number): void => {
    const deltaSeconds = Math.min(
      RIBBON_CONFIG.motion.maxDelta,
      Math.max(0, (timestamp - this.lastFrameTime) / 1000),
    )
    this.lastFrameTime = timestamp

    if (!document.hidden) {
      if (this.reducedMotion) {
        this.currentProgress = this.targetProgress
      } else {
        this.currentProgress = dampProgress(
          this.currentProgress,
          this.targetProgress,
          this.tuning.progressDamping,
          deltaSeconds,
        )
        this.time += deltaSeconds
        this.uniforms.uTime.value = this.time
      }

      this.chain.step(
        deltaSeconds,
        this.currentProgress,
        this.reducedMotion,
      )
      this.uploadChainTextures()
      this.render()
      this.frameTimes.push(deltaSeconds * 1000)

      if (this.frameTimes.length > 180) {
        this.frameTimes.shift()
      }
    }

    this.animationFrame = requestAnimationFrame(this.animate)
  }

  private createDepthUniforms(): RibbonUniforms {
    return {
      ...this.uniforms,
      uFabricNormalMap: this.uniforms.uFabricNormalMap,
      uFabricRepeat: this.uniforms.uFabricRepeat,
      uFabricStrength: this.uniforms.uFabricStrength,
      uBaseColor: this.uniforms.uBaseColor,
      uLightDirection: this.uniforms.uLightDirection,
      uAmbient: this.uniforms.uAmbient,
      uSpecularPower: this.uniforms.uSpecularPower,
    }
  }

  private measureNdcExtents(): readonly [number, number] {
    const positions = this.chain.getPositions()
    const frames = this.chain.getFrames()
    const halfWidth = RIBBON_CONFIG.geometry.width * 0.5
    let extentX = 0
    let extentY = 0

    this.group.updateMatrixWorld(true)

    for (
      let joint = 0;
      joint < RIBBON_CONFIG.geometry.joints;
      joint += 2
    ) {
      const offset = joint * 3

      for (const side of [-1, 1]) {
        this.ndcScratch.set(
          positions[offset] + frames[offset] * halfWidth * side,
          positions[offset + 1] + frames[offset + 1] * halfWidth * side,
          positions[offset + 2] + frames[offset + 2] * halfWidth * side,
        )
        this.ndcScratch.applyMatrix4(this.group.matrixWorld)
        this.ndcScratch.project(this.camera)
        extentX = Math.max(extentX, Math.abs(this.ndcScratch.x))
        extentY = Math.max(extentY, Math.abs(this.ndcScratch.y))
      }
    }

    return [extentX, extentY]
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera)
  }

  private uploadChainTextures(): void {
    this.chainTexture.needsUpdate = true
    this.frameTexture.needsUpdate = true
  }
}
